import Dice from './Dice.jsx';
import { COLOR_HEX } from './ludoBoard.js';

export function getFirstName(name) {
  if (!name?.trim()) return 'Player';
  return name.trim().split(/\s+/)[0];
}

export function formatRollTimer(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  if (total >= 60) {
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  }
  return `${total}s`;
}

function PlayerPin({ color }) {
  const fill = COLOR_HEX[color] || COLOR_HEX.blue;
  return (
    <span className="ludo-pvp-pin" aria-hidden>
      <svg viewBox="0 0 40 48" className="ludo-pvp-pin__svg">
        <circle cx="20" cy="14" r="11" fill={fill} stroke="#fff" strokeWidth="2" />
        <path
          d="M20 22 C14 28 8 38 8 44 L32 44 C32 38 26 28 20 22 Z"
          fill={fill}
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export default function LudoActionBar({
  myName,
  opponentName,
  myColor,
  opponentColor,
  dice,
  isRolling,
  onRoll,
  diceDisabled,
  diceColor,
  showTurnArrow,
  moveHint,
  secondsLeft,
  rollSecondsLeft,
  opponentRollSecondsLeft,
  rollGraceSecondsLeft,
  opponentRollGraceSecondsLeft,
  phase,
  myTimeoutCount,
  opponentTimeoutCount,
  maxMoveTimeouts,
  isMyTurn,
  mustMove,
}) {
  const showMoveTimer = mustMove && secondsLeft != null;
  const showMyRollTimer = isMyTurn && phase === 'ROLL' && rollSecondsLeft != null && !mustMove;
  const showOpponentRollTimer = !isMyTurn && phase === 'ROLL' && opponentRollSecondsLeft != null;
  const displaySeconds = showMyRollTimer
    ? rollSecondsLeft
    : showOpponentRollTimer
      ? opponentRollSecondsLeft
      : (showMoveTimer ? secondsLeft : null);
  const showTimer = displaySeconds != null;
  const timeoutWarning = isMyTurn && myTimeoutCount === maxMoveTimeouts - 1;
  const rollUrgent = (showMyRollTimer && rollSecondsLeft <= 30)
    || (showOpponentRollTimer && opponentRollSecondsLeft <= 30);
  const moveUrgent = showMoveTimer && secondsLeft <= 5;

  const hintText = moveHint
    ? `${moveHint}${timeoutWarning ? ' · Next timeout = you lose!' : ''}`
    : isMyTurn && phase === 'ROLL' && !mustMove
      ? showMyRollTimer
        ? `Roll the dice within ${formatRollTimer(rollSecondsLeft)} or you lose!`
        : rollGraceSecondsLeft != null
          ? `Your turn — roll now (${formatRollTimer(rollGraceSecondsLeft)} until timer starts)`
          : isRolling
            ? 'Rolling…'
            : 'Your turn — roll the dice'
      : !isMyTurn && phase === 'ROLL' && !isRolling
        ? showOpponentRollTimer
          ? `${opponentName} must roll in ${formatRollTimer(opponentRollSecondsLeft)} — you win if they don't!`
          : opponentRollGraceSecondsLeft != null
            ? `Waiting for ${opponentName} to roll (${formatRollTimer(opponentRollGraceSecondsLeft)} left)`
            : ''
        : isRolling
          ? 'Opponent rolling…'
          : '';

  return (
    <div className="ludo-pvp-action-wrap">
      <div
        className={`ludo-pvp-turn-arrow${showTurnArrow ? '' : ' ludo-pvp-turn-arrow--hidden'}`}
        aria-hidden={!showTurnArrow}
      >
        <svg viewBox="0 0 24 24" className="ludo-pvp-turn-arrow__svg">
          <path
            d="M12 6v10M8 12l4 4 4-4"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>

      <div className="ludo-pvp-action-bar">
        <div className={`ludo-pvp-player ludo-pvp-player--me${showTurnArrow ? ' ludo-pvp-player--active' : ''}`}>
          <PlayerPin color={myColor} />
          <span className="ludo-pvp-player__name">{myName}</span>
          {myTimeoutCount > 0 && (
            <span className="ludo-pvp-timeout-badge" title="Slow timeouts this game">
              ⏱ {myTimeoutCount}/{maxMoveTimeouts}
            </span>
          )}
        </div>

        <div className="ludo-pvp-dice-col">
          <div className="ludo-pvp-timer-slot">
            {showTimer && (
              <div className={`ludo-pvp-timer${rollUrgent || moveUrgent ? ' ludo-pvp-timer--urgent' : ''}`}>
                {formatRollTimer(displaySeconds)}
              </div>
            )}
          </div>
          <Dice
            value={dice}
            isRolling={isRolling}
            onRoll={onRoll}
            disabled={diceDisabled}
            color={diceColor}
            className="ludo-dice--bar"
          />
        </div>

        <div className={`ludo-pvp-player ludo-pvp-player--opp${showOpponentRollTimer ? ' ludo-pvp-player--active' : ''}`}>
          {opponentTimeoutCount > 0 && (
            <span className="ludo-pvp-timeout-badge ludo-pvp-timeout-badge--opp">
              ⏱ {opponentTimeoutCount}/{maxMoveTimeouts}
            </span>
          )}
          <span className="ludo-pvp-player__name">{opponentName}</span>
          <PlayerPin color={opponentColor} />
        </div>
      </div>

      <p className="ludo-pvp-move-hint">{hintText}</p>
    </div>
  );
}
